import { prefersReducedMotion } from '@theme/utilities';

/**
 * Recipe article hero video.
 *
 * The first thing on a recipe article should be the dish being made, moving — not a still
 * frame with a play button on it, which is what the block rendered before: `controls` and
 * `preload="metadata"` and nothing else, so it sat there looking like a photograph until
 * someone thought to press it.
 *
 * WHY JS STARTS PLAYBACK INSTEAD OF THE autoplay ATTRIBUTE
 *
 * The project's motion contract (same as assets/kurashi-reels.js) is that
 * prefers-reduced-motion means no preview playback at all. With `autoplay` in the markup the
 * browser starts the video before any script runs, so a reduced-motion visitor would see a
 * second of movement and then a pause — the exact thing they asked not to happen. Starting
 * from here means they never see it move.
 *
 * The fallback with no JS is still correct rather than broken: the video keeps `controls`,
 * `playsinline` and its poster, so it reads as a normal video the visitor can press.
 *
 * Playback also stops once the video scrolls out of view. A muted loop nobody is looking at
 * is just battery and bandwidth, and these files are phone-shot recipe clips, not thumbnails.
 */

const ROOT_MARGIN = '0px';
const VISIBLE_ENOUGH = 0.35;

class KurashiRecipeVideo extends HTMLElement {
  connectedCallback() {
    this.video = this.querySelector('video');
    if (!this.video) return;

    // Controls stay on: this is the page's main media, and someone who wants to scrub back to
    // the folding step should be able to. Muted is what makes unattended playback allowed at
    // all — every browser blocks audible autoplay — and it is set here as well as in the
    // markup because a cached DOM can come back with the property flipped.
    this.video.muted = true;
    this.video.loop = true;

    if (prefersReducedMotion()) {
      this.setAttribute('data-preview', 'off');
      return;
    }

    this.watchForUndecodableTrack();

    this.onIntersect = this.onIntersect.bind(this);
    this.observer = new IntersectionObserver(this.onIntersect, {
      rootMargin: ROOT_MARGIN,
      threshold: [0, VISIBLE_ENOUGH],
    });
    this.observer.observe(this);

    // Once the visitor presses pause, stop overriding them for the rest of the visit.
    this.video.addEventListener('pause', () => {
      if (!this.video.ended && this.userWantsItStopped !== false) this.userPaused = true;
    });
    this.video.addEventListener('play', () => {
      this.userPaused = false;
    });
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }

  /**
   * Falls back to the poster when the file has no video track this browser can decode.
   *
   * This is not the same failure as a missing file, and it does NOT fire an `error` event —
   * which is what makes it worth handling explicitly. A QuickTime .mov carrying HEVC (the
   * iPhone "High Efficiency" default) parses far enough for `loadedmetadata` to fire, so the
   * element reports itself as ready and even advances currentTime, while decoding nothing:
   * videoWidth stays 0 and the frame stays black. Measured on this shop's own upload —
   * canPlayType('video/quicktime') and canPlayType('video/mp4; codecs="hvc1"') both return
   * the empty string in Chrome.
   *
   * Marking the host lets the CSS show the poster instead of a black rectangle, so a wrongly
   * encoded upload degrades to a still photograph rather than to a hole in the page.
   */
  watchForUndecodableTrack() {
    const check = () => {
      if (this.video.videoWidth === 0 && this.video.readyState >= 1) {
        this.setAttribute('data-undecodable', '');
        this.video.pause();
      } else {
        this.removeAttribute('data-undecodable');
      }
    };
    this.video.addEventListener('loadedmetadata', check);
    this.video.addEventListener('loadeddata', check);
    this.video.addEventListener('error', () => this.setAttribute('data-undecodable', ''));
  }

  onIntersect(entries) {
    for (const entry of entries) {
      if (entry.isIntersecting && entry.intersectionRatio >= VISIBLE_ENOUGH) {
        if (this.userPaused) continue;
        // play() rejects when the browser declines (a data-saver setting, a power-saving mode,
        // an unmuted state we did not expect). That is a fine outcome — the poster and the
        // controls are still there — so the rejection is swallowed rather than logged.
        this.video.play().catch(() => {});
        this.setAttribute('data-preview', 'playing');
      } else if (!entry.isIntersecting) {
        this.video.pause();
        // Cleared so leaving and re-entering the viewport resumes, which a scroll-driven
        // pause should not be able to latch off permanently.
        this.userPaused = false;
        this.setAttribute('data-preview', 'paused');
      }
    }
  }
}

if (!customElements.get('kurashi-recipe-video')) {
  customElements.define('kurashi-recipe-video', KurashiRecipeVideo);
}
